import React, {
  FunctionComponent,
  useEffect,
  useState,
  useContext,
} from "react";
import { AppPage, PrintButton, LoadingMask } from "../../../components";
import { ArrowIcon, Button } from "@cmsgov/ds-medicare-gov";
import { withRouter, RouteComponentProps } from "react-router";
import {
  MedigapPlanType,
  MedigapPlan,
  getMedigapPlansForState,
  MedigapPlanPremiumRange,
  getMedigapPricingYear,
} from "../utils/plans";
import { useTranslate } from "../../../helpers";
import { getMedigapPlanPremiumRanges } from "../../../api";
import { AppContext } from "../../../app/store";
import { Link } from "react-router-dom";
import routes from "../../../app/routes";
import PlanNotes from "./PlanNotes";
import WhatYouWillPay from "./WhatYouWillPay";
import PartAServices from "./PartAServices";
import PartAandBServices from "./PartAandBServices";
import PartBServices from "./PartBServices";
import ExtraBenefits from "./ExtraBenefits";
import { useFlags } from "launchdarkly-react-client-sdk";
import { MedigapPlanDetailsErrorPage } from "../components/MedigapPlanDetailsErrorPage";

export interface MedigapPlanDetailsSection {
  title: string | JSX.Element;
  tables: JSX.Element[];
}

interface MedigapPlanDetailsState {
  cachedConvertedPlanType: string;
  hasError: boolean;
  isLoading: boolean;
  planData?: MedigapPlan;
  planPremiumRanges?: MedigapPlanPremiumRange;
  planType?: MedigapPlanType;
  sections: MedigapPlanDetailsSection[];
}

const MedigapPlanDetailsPage: FunctionComponent<RouteComponentProps> = ({
  match,
  history,
}) => {
  const [medigapPlanDetailsState, setMedigapPlanDetailsState] = useState<
    MedigapPlanDetailsState
  >({
    cachedConvertedPlanType: "",
    hasError: false,
    isLoading: false,
    sections: [],
  });
  const {
    cachedConvertedPlanType,
    hasError,
    isLoading,
    planData,
    planPremiumRanges,
    planType,
    sections,
  } = medigapPlanDetailsState;

  const {
    state: { county, zipcode, demographicInfo },
  } = useContext(AppContext);
  const t = useTranslate();
  const flags = useFlags();
  const { feEnableNextYearMedigapPricing, mctCurrentPlanYear } = flags;

  useEffect(() => {
    const { medigapPlanType } = match.params as { medigapPlanType: string };
    const convertedPlanType = medigapPlanType
      .trim()
      .toUpperCase() as MedigapPlanType;

    if (cachedConvertedPlanType !== convertedPlanType) {
      setMedigapPlanDetailsState(state => ({
        ...state,
        cachedConvertedPlanType: convertedPlanType,
        hasError: false,
        isLoading: true,
      }));
      if (
        Object.values(MedigapPlanType).includes(convertedPlanType) &&
        zipcode &&
        county
      ) {
        setMedigapPlanDetailsState(state => ({
          ...state,
          planType: convertedPlanType,
        }));

        const { state } = county;

        const data = getMedigapPlansForState({
          state,
          pricingYear: getMedigapPricingYear({
            feEnableNextYearMedigapPricing,
            mctCurrentPlanYear,
          }),
        }).find(plan => plan.plan_type === convertedPlanType);

        setMedigapPlanDetailsState(state => ({ ...state, planData: data }));
        try {
          getMedigapPlanPremiumRanges(state, zipcode, demographicInfo).then(
            data => {
              setMedigapPlanDetailsState(state => ({
                ...state,
                planPremiumRanges: data.find(
                  plan => plan.medigap_plan_type === convertedPlanType
                ),
              }));
            }
          );
        } catch (err) {
          setMedigapPlanDetailsState(state => ({ ...state, hasError: true }));
        }
      } else {
        setMedigapPlanDetailsState(state => ({ ...state, hasError: true }));
      }
      setMedigapPlanDetailsState(state => ({ ...state, isLoading: false }));
    }
  }, [
    match.params,
    zipcode,
    county,
    feEnableNextYearMedigapPricing,
    mctCurrentPlanYear,
  ]);

  useEffect(() => {
    if (planType && planData && planPremiumRanges) {
      const generalCostsSection = {
        title: t("medigap.plan_details.general_costs"),
        tables: [
          <WhatYouWillPay
            key="what-you-will-pay"
            planType={planType}
            planData={planData}
            planPremiumRanges={planPremiumRanges}
          />,
        ],
      };

      const coveredServicesSection = {
        title: t("medigap.plan_details.covered_services"),
        tables: [
          <PartAServices
            key="part-a-services"
            planType={planType}
            planData={planData}
          />,
          <PartAandBServices key="part-a-and-b-services" planData={planData} />,
          <PartBServices
            key="part-b-services"
            planType={planType}
            planData={planData}
          />,
          <ExtraBenefits
            key="extra-benefits"
            planData={planData}
            planType={planType}
          />,
        ],
      };
      setMedigapPlanDetailsState(state => ({
        ...state,
        sections: [generalCostsSection, coveredServicesSection],
      }));
    }
  }, [planType, planData, planPremiumRanges]);

  if (hasError) {
    return <MedigapPlanDetailsErrorPage />;
  }

  return (
    <AppPage title={planData ? t(planData.plan_details_name) : ""} hideLogin>
      <LoadingMask loading={isLoading} delay={500} />
      <div className="ds-u-margin-top--2 ds-l-row ds-u-justify-content--between mct-hide-print">
        <Link
          to={routes.medigap.plans}
          onClick={(): void => {}}
          className="ds-c-button ds-c-button--transparent PlanDetails__backButton"
        >
          <ArrowIcon
            direction="left"
            className="ds-u-valign--middle ds-u-margin-right--1"
          />
          {t("medigap.plan_details.go_back")}
        </Link>

        <PrintButton />
      </div>
      <div className="MedigapPlanDetails__header">
        {planData && <h1>{t(planData.plan_details_name)}</h1>}
        <div className="MedigapPlanDetails__viewPolicies">
          {planType && (
            <Button
              variation="primary"
              onClick={(): void =>
                history.push(
                  routes.medigap.policies.replace(
                    ":medigapPlanType",
                    planType?.toString() || cachedConvertedPlanType
                  )
                )
              }
              className="MedigapPlanDetails__viewPoliciesButton mct-hide-print"
            >
              {t("medigap.plan_details.view_policies")}
            </Button>
          )}
        </div>
      </div>

      {planData && <PlanNotes plan={planData} />}

      {sections.map((section, i) => (
        <div key={i} className="MedigapPlanDetails__section">
          <h2 className="MedigapPlanDetails__sectionTitle">{section.title}</h2>

          <div className="MedigapPlanDetails__tableContainer">
            {section.tables.map(table => table)}
          </div>
        </div>
      ))}
    </AppPage>
  );
};

export default withRouter(MedigapPlanDetailsPage);
